/**
 * teamSelection.ts — client half of MULTI-TEAM selection (Slice 2).
 * See ~/workspace/isats_multiteam/ANALYSIS_AND_PLAN.md §(a)/(c).
 *
 * TERMINOLOGY (owner ruling, Oct 15 2026): a "team" here is an
 * ORGANIZATION team — one org doc in `referrers` / `employers` /
 * `sponsors` (Referrer Team / Sponsor Team / Employer Team). Never the
 * workspace staff team. Selecting a team switches the active ORG
 * context inside the one portal workspace the user signed into (D-8:
 * the tenant is fixed by the host and is never switched by team
 * selection).
 *
 * The selection is a UX convenience, never a credential: it is stored
 * per role for the SESSION only (F-MT-1: sessionStorage, mirroring
 * `isats_selected_role`) and the server re-verifies membership +
 * workspace access on every request (Slice 1 resolvers). This module
 * is dependency-free on purpose — `src/lib/api.ts` (safeFetch) imports
 * it for the central plumbing, so it must never import back.
 */

export type OrgTeamRole = 'referrer' | 'employer' | 'sponsor';

/** One eligible team, exactly as `GET /api/{role}/teams` returns it. */
export interface EligibleTeam {
  id: string;
  name: string;
  /** The caller's rank inside this team (display + in-team surfaces). */
  role: 'super' | 'member';
  /** The org doc's own status (e.g. a restricted referrer org reads 'deactivated'). */
  status: string;
}

/** The query parameter each role's Slice 1 resolver reads. */
export const TEAM_SELECTION_PARAM: Record<OrgTeamRole, string> = {
  referrer: 'referrerId',
  employer: 'employerId',
  sponsor: 'sponsorId',
};

const STORAGE_PREFIX = 'isats_selected_team:';

export function teamSelectionStorageKey(role: OrgTeamRole): string {
  return `${STORAGE_PREFIX}${role}`;
}

export function getStoredTeamSelection(role: OrgTeamRole): string | null {
  try {
    const v = sessionStorage.getItem(teamSelectionStorageKey(role));
    return v && v.trim() ? v : null;
  } catch {
    return null; // storage unavailable (private mode / SSR) — never fatal
  }
}

export function setStoredTeamSelection(role: OrgTeamRole, teamId: string): void {
  try {
    if (teamId) sessionStorage.setItem(teamSelectionStorageKey(role), teamId);
  } catch { /* storage unavailable — the in-memory gate state still works */ }
}

export function clearStoredTeamSelection(role: OrgTeamRole): void {
  try {
    sessionStorage.removeItem(teamSelectionStorageKey(role));
  } catch { /* storage unavailable */ }
}

/** The path portion of an API URL (query/hash stripped), or null. */
function apiPathOf(url: string): string | null {
  if (typeof url !== 'string' || !url) return null;
  if (url.startsWith('/')) {
    const end = url.search(/[?#]/);
    return end === -1 ? url : url.slice(0, end);
  }
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

/** The org role a path belongs to, or null for non-org API paths. */
export function orgRoleForApiPath(path: string | null): OrgTeamRole | null {
  if (!path) return null;
  if (path.startsWith('/api/referrer/')) return 'referrer';
  if (path.startsWith('/api/employer/')) return 'employer';
  if (path.startsWith('/api/sponsor/')) return 'sponsor';
  return null;
}

/**
 * Stay-implicit endpoints (Slice 1 census §b): role-level questions
 * answered BEFORE any selection exists, own-org creation during
 * onboarding, and pointer-based enrichment. They must never gain the
 * selection param — their server handlers do not resolve a team.
 */
function isTeamSelectionExemptPath(role: OrgTeamRole, path: string): boolean {
  if (path === `/api/${role}/teams`) return true; // the enumeration itself
  if (path === `/api/${role}/onboard-status`) return true; // role-level status (PR-13)
  if (path === `/api/${role}/onboard`) return true; // creates the caller's OWN org
  if (role === 'sponsor' && path === '/api/sponsor/status') return true;
  if (role === 'sponsor' && path === '/api/sponsor/profile') return true; // pointer-based
  if (role === 'referrer' && path === '/api/referrer/check-email') return true; // resolves no org
  return false;
}

/**
 * Attach the stored team selection for the URL's role as the query
 * parameter the Slice 1 resolvers read. Returns the URL unchanged when:
 * the URL is not an org-scoped API path (applicant/admin/public calls
 * NEVER gain the param), the endpoint is stay-implicit, no selection
 * is stored for the role, or the URL already carries the role's param
 * (the employer dashboards' own wrappers append it from component
 * state — exactly one param, never a duplicate).
 */
export function withTeamSelection(url: string): string {
  const path = apiPathOf(url);
  const role = orgRoleForApiPath(path);
  if (!role || !path) return url;
  if (isTeamSelectionExemptPath(role, path)) return url;
  const param = TEAM_SELECTION_PARAM[role];

  const hashIdx = url.indexOf('#');
  const hash = hashIdx === -1 ? '' : url.slice(hashIdx);
  const noHash = hashIdx === -1 ? url : url.slice(0, hashIdx);
  const qIdx = noHash.indexOf('?');
  const query = qIdx === -1 ? '' : noHash.slice(qIdx + 1);
  if (query) {
    try {
      if (new URLSearchParams(query).has(param)) return url;
    } catch { /* malformed query — fall through and append plainly */ }
  }
  const selection = getStoredTeamSelection(role);
  if (!selection) return url;
  const sep = qIdx === -1 ? '?' : '&';
  return `${noHash}${sep}${param}=${encodeURIComponent(selection)}${hash}`;
}

/** Dispatched on `window` when the server refuses the active selection. */
export const TEAM_SELECTION_INVALID_EVENT = 'team:selection-invalid';

/**
 * Pure decision: does this failed response mean the role's team
 * selection is unusable? Exactly two server answers say so (Slice 1
 * contract): 409 TEAM_SELECTION_REQUIRED (multi-team, no usable
 * selection) and 403 TEAM_NOT_MEMBER (the sent selection does not
 * verify). Every other failure — including other 403s such as
 * REFERRER_RESTRICTED — leaves the selection alone.
 */
export function teamSelectionFailureRole(
  url: string,
  status: number,
  data: any,
): OrgTeamRole | null {
  const path = apiPathOf(url);
  const role = orgRoleForApiPath(path);
  if (!role || !path) return null;
  if (isTeamSelectionExemptPath(role, path)) return null;
  const code = data?.errorCode;
  if (status === 409 && code === 'TEAM_SELECTION_REQUIRED') return role;
  if (status === 403 && code === 'TEAM_NOT_MEMBER') return role;
  return null;
}

/**
 * Side-effecting companion used by safeFetch: on a team-selection
 * refusal, drop the stored selection (it grants nothing — the server
 * verifies per request — but keeping it would re-send a dead id on
 * every call) and notify the mounted TeamSelectionGate, which
 * re-resolves the eligible list and lands the user gracefully.
 */
export function noteTeamSelectionFailure(url: string, status: number, data: any): void {
  const role = teamSelectionFailureRole(url, status, data);
  if (!role) return;
  clearStoredTeamSelection(role);
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(TEAM_SELECTION_INVALID_EVENT, { detail: { role } }));
    }
  } catch { /* no window (tests/SSR) — the cleared storage is the durable part */ }
}
